import React from 'react';
import { Code, Brain, Globe, Database } from 'lucide-react';
import { skills } from '../data/mock';

const Skills = () => {
  const skillCategories = [
    {
      title: "Programming Languages",
      icon: Code,
      skills: skills.languages,
      color: "text-[rgb(218,255,1)]"
    },
    {
      title: "ML/DL Frameworks",
      icon: Brain,
      skills: skills.frameworks,
      color: "text-[rgb(218,255,1)]"
    },
    {
      title: "Web & Tools", 
      icon: Globe,
      skills: skills.webTools,
      color: "text-[rgb(218,255,1)]"
    },
    {
      title: "ML/DL Topics",
      icon: Database,
      skills: skills.mlTopics,
      color: "text-[rgb(218,255,1)]"
    }
  ];

  return (
    <section id="skills" className="py-20 bg-gradient-to-br from-[rgb(26,28,30)] to-[rgb(17,17,19)] relative overflow-hidden">
      {/* Glassmorphism background elements */}
      <div className="absolute top-1/4 left-10 w-36 h-36 rounded-full bg-gradient-to-br from-[rgba(218,255,1,0.1)] to-transparent backdrop-blur-sm opacity-40"></div>
      <div className="absolute bottom-1/4 right-10 w-28 h-28 rounded-full bg-gradient-to-tl from-[rgba(218,255,1,0.08)] to-transparent backdrop-blur-sm opacity-30"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header with glass effect */}
          <div className="text-center mb-16">
            <div className="inline-block glass-light px-6 py-2 rounded-full mb-6">
              <span className="text-[rgb(218,255,1)] text-sm font-medium tracking-wide">MY EXPERTISE</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 relative">
              <span className="relative z-10">
                Technical <span className="text-[rgb(218,255,1)] relative">
                  Skills
                  <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-20 blur-xl rounded-lg"></div>
                </span>
              </span>
            </h2>
            <p className="text-xl text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
              A comprehensive toolkit for building intelligent systems and scalable applications
            </p>
          </div>

          {/* Skills Grid with enhanced glassmorphism */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {skillCategories.map((category, index) => (
              <div 
                key={index}
                className="group glass-card rounded-2xl p-8 hover:glass-card transition-all duration-300 hover:transform hover:translate-y-[-4px] hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] relative overflow-hidden"
              >
                {/* Top accent glow */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[rgb(218,255,1)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Category Header */}
                <div className="flex items-center space-x-4 mb-6 relative z-10">
                  <div className="glass-light w-14 h-14 rounded-2xl flex items-center justify-center text-[rgb(218,255,1)] group-hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300 group-hover:scale-110">
                    <category.icon size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                    {category.title}
                  </h3>
                </div>

                {/* Skills Tags with glass effect */}
                <div className="flex flex-wrap gap-3 relative z-10">
                  {category.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="glass-light px-4 py-2 text-[rgb(218,218,218)] rounded-lg text-sm font-medium hover:glass-card hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] hover:shadow-[0_0_15px_rgba(218,255,1,0.2)] transition-all duration-200 cursor-default"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                {/* Subtle shimmer effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[rgba(218,255,1,0.05)] to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-out"></div>
              </div>
            ))}
          </div>

          {/* Proficiency Levels with enhanced glassmorphism */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold text-white mb-2">
                Proficiency <span className="text-[rgb(218,255,1)]">Levels</span>
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { level: "Expert", size: "w-8 h-8", skills: "Python, TensorFlow, PyTorch, Computer Vision" },
                { level: "Advanced", size: "w-6 h-6", skills: "React.js, FastAPI, Django, Deep Learning" },
                { level: "Intermediate", size: "w-4 h-4", skills: "C/C++, SQL, Redis, DevOps" }
              ].map((proficiency, index) => (
                <div key={index} className="glass-card rounded-2xl p-6 text-center hover:glass-card transition-all duration-300 hover:transform hover:scale-105 group">
                  <div className="w-16 h-16 mx-auto mb-4 glass-light rounded-full flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300">
                    <div className={`${proficiency.size} rounded-full bg-[rgb(218,255,1)] shadow-[0_0_10px_rgba(218,255,1,0.5)] group-hover:shadow-[0_0_15px_rgba(218,255,1,0.8)] transition-all duration-300`}></div>
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                    {proficiency.level}
                  </h4>
                  <p className="text-sm text-[rgb(161,161,170)]">{proficiency.skills}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;